import { Component, type ReactNode, Suspense, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { BrowserRouter, Route, Routes } from "react-router-dom";
import { exchangeForCustomToken, signIntoFirebase } from "./firebase/auth.js";
import { initI18n } from "./i18n/init.js";
import { loadEnv } from "./lib/env.js";
import Lobby from "./routes/lobby.js";
import Sheet from "./routes/sheet.js";
import Table from "./routes/table.js";
import { getDiscordAuth } from "./sdk/discord.js";
import { useAuthStore } from "./stores/auth.js";

initI18n();

type AuthStage =
  | "init"
  | "discord-ready"
  | "authorize"
  | "oauth-exchange"
  | "mint"
  | "firebase-signin"
  | "done";

function AuthBoot({ children }: { children: React.ReactNode }) {
  const { t } = useTranslation();
  const ready = useAuthStore((s) => s.ready);
  const setAuth = useAuthStore((s) => s.setAuth);
  const [error, setError] = useState<string | null>(null);
  const [stage, setStage] = useState<AuthStage>("init");

  useEffect(() => {
    if (ready) return;
    let cancelled = false;
    (async () => {
      try {
        const env = loadEnv();
        setStage("discord-ready");
        const discord = await getDiscordAuth({
          clientId: env.discordClientId,
          tokenExchange: async ({ code }) => {
            setStage("oauth-exchange");
            const res = await fetch(`${env.authBrokerUrl}/oauth/token`, {
              method: "POST",
              headers: { "content-type": "application/json" },
              body: JSON.stringify({ code }),
            });
            if (!res.ok) {
              const text = await res.text();
              throw new Error(`oauth token exchange failed: ${res.status} ${text.slice(0, 200)}`);
            }
            return (await res.json()) as { access_token: string };
          },
          onAuthorize: () => setStage("authorize"),
        });
        setStage("mint");
        const mint = await exchangeForCustomToken({
          brokerUrl: `${env.authBrokerUrl}/mint`,
          discordAccessToken: discord.accessToken,
        });
        setStage("firebase-signin");
        await signIntoFirebase(mint.firebase_custom_token);
        if (cancelled) return;
        setStage("done");
        setAuth(discord.user.id, null);
      } catch (e) {
        if (!cancelled) {
          const msg =
            e instanceof Error
              ? e.message
              : typeof e === "string"
                ? e
                : (() => {
                    try {
                      return JSON.stringify(e);
                    } catch {
                      return "unknown error";
                    }
                  })();
          setError(msg);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [ready, setAuth]);

  if (error)
    return <p className="p-4 text-destructive">{t("app.auth.failed", { reason: error })}</p>;
  if (!ready)
    return (
      <p className="p-4">
        {t("app.auth.connecting")} <span className="opacity-60">[{stage}]</span>
      </p>
    );
  return <>{children}</>;
}

class ErrorBoundary extends Component<{ children: ReactNode }, { error: Error | null }> {
  override state = { error: null as Error | null };
  static getDerivedStateFromError(error: Error) {
    return { error };
  }
  override componentDidCatch(error: Error, info: { componentStack?: string | null }) {
    console.error("Activity render error", error, info.componentStack);
  }
  override render() {
    if (this.state.error) {
      return (
        <div className="p-4 text-destructive">
          <p className="font-bold">render error</p>
          <pre className="text-xs whitespace-pre-wrap">{this.state.error.message}</pre>
          <pre className="text-xs whitespace-pre-wrap opacity-60">
            {this.state.error.stack?.split("\n").slice(0, 6).join("\n")}
          </pre>
        </div>
      );
    }
    return this.props.children;
  }
}

export default function App() {
  return (
    <BrowserRouter>
      <Suspense fallback={null}>
        <ErrorBoundary>
          <AuthBoot>
            <Routes>
              <Route path="/" element={<Lobby />} />
              <Route path="/table" element={<Table />} />
              <Route path="/sheet/:charId" element={<Sheet />} />
            </Routes>
          </AuthBoot>
        </ErrorBoundary>
      </Suspense>
    </BrowserRouter>
  );
}
